import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import urlJoin from 'url-join';
import { normalizeMediaObject, getImageByWidth } from 'api';
import { defaultMapStyle } from 'app-constants';
import { useAnalytics } from 'hooks';
import { Route, Switch } from 'react-router-dom';
import ResizeDetector from 'react-resize-detector';
import { Slideshow } from 'msp-components';
import Error404 from 'components/Error404';

const normalizeSlideData = (slides, imgWidth = 1500) => slides.map(slide => {
  const type = slide.media.type;
  const imageSource = type === 'video' ? slide.media.previewImages.posterFrame : slide.media.versions;
  const imageUrl = getImageByWidth(imageSource, imgWidth).url;
  const thumbnailUrl = getImageByWidth(imageSource, 300).url;
  const mapZoom = isNaN(parseInt(slide.mapZoom, 10)) ? null : parseInt(slide.mapZoom, 10);

  const relatedItems = slide.relatedItems
    .filter(item => item.itemType !== 'media' || !!item.media)
    .map(item => {
      let media;
      if (item.media) {
        const aspectRatio = item.media.width && item.media.width / item.media.height;
        const isVertical = !!aspectRatio && aspectRatio < 1;
        const previewVersion = isVertical ? 'vertical' : 'posterFrame';
        const posterFrameImg = item.media.previewImages && getImageByWidth(item.media.previewImages[previewVersion], imgWidth);
        const posterFrame = posterFrameImg && posterFrameImg.url;

        media = {
          ...normalizeMediaObject(item.media),
          posterFrame,
          aspectRatio,
        };
      }

      return {
        id: item.uid,
        type: item.itemType === 'media' ? item.media.type : 'link',
        title: item.name,
        description: item.description,
        credit: item.credit,
        linkUrl: item.linkUrl,
        linkImage: item.linkImage && getImageByWidth(item.linkImage.versions, 600).url,
        media,
      };
    });

  return {
    id: slide.uid,
    type,
    title: slide.name,
    description: slide.description,
    credit: slide.credit,
    date: slide.date,
    latitude: slide.latitude,
    longitude: slide.longitude,
    placename: slide.placename,
    mapZoom,
    imageUrl,
    thumbnailUrl,
    media: type === 'video' ? normalizeMediaObject(slide.media) : null,
    licenseUrl: slide.licenseUrl,
    printsUrl: slide.printsUrl,
    bleed: slide.layout === 'bleed',
    relatedItems,
  };
});

const SlideshowTitle = ({ routeProps, canonicalPath, mapStyle, mapboxToken, displayOptions, slides, onSubItemChange }) => {
  const [containerWidth, setContainerWidth] = useState();
  const handleResize = width => setContainerWidth(width);

  const [activeSlideshowItemId, setActiveSlideshowItemId] = useState(null);
  useEffect(() => {
    if (activeSlideshowItemId) {
      const activeItem = (slides.find(({ uid }) => uid === activeSlideshowItemId) || {}).media;
      onSubItemChange(activeItem);
    } else {
      onSubItemChange(null);
    }
    return () => onSubItemChange(null);
  }, [activeSlideshowItemId, slides, onSubItemChange]);

  const { match, history } = routeProps;
  const titleBasePath = canonicalPath || match.url;

  const { name, date, caption, purchaseLinks, location, related } = displayOptions.menuItems;

  const slideshowItems = useMemo(() => normalizeSlideData(slides, containerWidth && containerWidth * 1.5), [slides, containerWidth]);
  const ids = slideshowItems.map(({ id }) => id);
  const handleItemSelect = id => history.push(urlJoin(titleBasePath, `${ids.indexOf(id) + 1}`));

  const { trackMediaPlay, trackMediaProgress } = useAnalytics();

  return (
    <Switch>
      <Route
        exact
        path={urlJoin(match.url, ':slideIndex?')}
        render={props => {
          // Validate slide index
          // eslint-disable-next-line
          const { slideIndex } = props.match.params;
          const index = parseInt(slideIndex || 1, 10);
          if (isNaN(index) || index < 1 || index > slides.length) return <Error404 />;

          const activeItemId = ids[index - 1];
          setActiveSlideshowItemId(activeItemId);
          // TODO loop option
          return (
            <>
              <ResizeDetector
                handleWidth
                onResize={handleResize}
                refreshMode="debounce"
                refreshRate={500}
              />
              <Slideshow
                hideTitle={name === false}
                hideDate={date === false}
                hideCaption={caption === false}
                hidePurchaseLinks={purchaseLinks === false}
                hideLocation={location === false}
                hideRelated={related === false}
                defaultPanel={displayOptions.menuDefaultPanel}
                items={slideshowItems}
                loop={false}
                mapStyle={mapStyle || defaultMapStyle}
                mapboxToken={mapboxToken}
                activeItemId={activeItemId}
                onSelectItem={handleItemSelect}
                onMediaPlay={trackMediaPlay}
                onMediaProgress={trackMediaProgress}
              />
            </>
          );
        }}
      />
      <Route component={Error404} />
    </Switch>
  );
};

SlideshowTitle.propTypes = {
  routeProps: PropTypes.object,
  canonicalPath: PropTypes.string,
  mapStyle: PropTypes.string,
  mapboxToken: PropTypes.string,
  displayOptions: PropTypes.shape({
    menuDefaultPanel: PropTypes.oneOf(['caption', 'location', 'related']),
    menuItems: PropTypes.shape({
      name: PropTypes.bool,
      date: PropTypes.bool,
      caption: PropTypes.bool,
      purchaseLinks: PropTypes.bool,
      location: PropTypes.bool,
      related: PropTypes.bool,
    }),
  }),
  slides: PropTypes.arrayOf(PropTypes.object),
  onSubItemChange: PropTypes.func
};

export default SlideshowTitle;
